import "react-day-picker/lib/style.css";

import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import ReactDayPicker, { DayModifiers } from "react-day-picker";
import styled, { css } from "styled-components";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { isSameDay } from "src/library/utils/date-helpers";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { Icons } from "src/library/components/Icon/types";
import { getOrderStatusFromId } from "src/core/orders/utils/orderStatus";
import {
  IOrderStatus,
  IOrder,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import { useOrder } from "src/core/orders/hooks";
import { useDrop } from "src/core/drops/hooks";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext/CreateOrderContext";

import Day, { DayDetails } from "./Day";
import { WeekDayHeaders } from "./WeekDayHeaders";

export interface DayWithDetail {
  details: DayDetails[];
  deliveriesDue: number;
  date: Date;
}

export interface IDayPickerProps {
  onDateClick: (date: Date, detail: DayWithDetail) => void;
  handleMonthChange: (month: Date) => void;
  date: Date;
  orders: IOrder[];
  loading: boolean;
  orderStatuses: IOrderStatus[];
}

export interface WarningDates {
  component: JSX.Element;
  date: Date;
}

export const OrdersCalendar: React.ReactFCWithChildren<IDayPickerProps> = ({
  handleMonthChange, orderStatuses, orders, date, loading, onDateClick
}) => {
  const [ datesWithDetail, setDatesWithDetail ] = React.useState<DayWithDetail[]>([]);
  const [ { isCreatingOrder }, { createOrder } ] = useOrder();
  const [ { drop } ] = useDrop();

  React.useEffect(() => {
    handleMonthChange(moment.utc().toDate());
  }, [ handleMonthChange ]);

  const orderToDetailDate = React.useCallback(
    (order: IOrder): DayWithDetail | null => {
      const orderStatus = getOrderStatusFromId(orderStatuses, order.orderStatusId);

      if (orderStatus && order.orderDeliveryDate) {
        return {
          date: new Date(order.orderDeliveryDate),
          details: [
            {
              status: orderStatus,
              qty: 1,
              id: order.id
            }
          ],
          deliveriesDue: orderStatus.settings.group === "DUE" ? 1 : 0
        };
      }

      return null;
    },
    [ orderStatuses ]
  );

  React.useEffect(() => {
    const detailDates: DayWithDetail[] = [];

    for (const order of orders) {
      if (order.orderDeliveryDate) {
        // @ts-ignore - we check that order.orderDeliveryDate isn't null
        const dateIndex = _.findIndex(detailDates, day => isSameDay(day.date, new Date(order.orderDeliveryDate)));
        const detailDate = orderToDetailDate(order);

        if (detailDate) {
          if (dateIndex === -1) {
            detailDates.push(detailDate);
          } else {
            detailDates[ dateIndex ].deliveriesDue = detailDates[ dateIndex ].deliveriesDue + detailDate.deliveriesDue;

            if (detailDate.details[ 0 ]) {
              const typeIndex = _.findIndex(
                detailDates[ dateIndex ].details,
                detail => detail.status.settings.group === detailDate.details[ 0 ].status.settings.group
              );

              if (typeIndex === -1) {
                detailDates[ dateIndex ].details.push(detailDate.details[ 0 ]);
              } else {
                detailDates[ dateIndex ].details[ typeIndex ].qty++;
              }
            }
          }
        }
      }
    }

    setDatesWithDetail(detailDates);
  }, [ orders ]);

  const modifiers = { hasInfo: (day: Date) => (_.find(datesWithDetail, detail => isSameDay(day, detail.date)) ? true : false) };

  const handleDayClick = (day: Date, detail?: DayWithDetail) => {
    if (detail) {
      onDateClick(day, detail);
    }

    return;
  };

  return (
    <Wrapper>
      <StyledPicker
        initialMonth={date}
        firstDayOfWeek={1}
        numberOfMonths={1}
        modifiers={modifiers}
        showWeekDays={false}
        captionElement={() => null}
        navbarElement={(props: any) => {
          const {
            month, onPreviousClick, onNextClick
          } = props;

          return (
            <>
              <MonthNavigation>
                <div className="month-navigation-inner">
                  <ThisWeek>
                    <Text
                      className="month-name"
                      fontSize="h2"
                    >
                      <b>
                        {moment.utc(month).format("MMMM YYYY")}
                      </b>
                    </Text>
                  </ThisWeek>

                  <NavArrowWrapper onClick={() => onPreviousClick()}>
                    <Icon icon={Icons.CHEVRONLEFT} />
                  </NavArrowWrapper>

                  <NavArrowWrapper onClick={() => onNextClick()}>
                    <Icon icon={Icons.CHEVRONRIGHT} />
                  </NavArrowWrapper>
                </div>

                <div className="order-button">
                  <CreateOrderContext
                    direction="left"
                    key={0}
                    drop={drop}
                    loading={isCreatingOrder}
                    createDraftOrder={deliveryDate => createOrder(deliveryDate, OrderOrigin.Manual)}
                  />
                </div>
              </MonthNavigation>

              <WeekDayHeaders />
            </>
          );
        }}
        onMonthChange={handleMonthChange}
        showOutsideDays
        enableOutsideDaysClick={false}
        renderDay={(date, modifiers: DayModifiers) => {
          const dayDetail = _.find(datesWithDetail, detail => isSameDay(date, detail.date));

          return (
            <Day
              handleDayClick={() => handleDayClick(date, dayDetail)}
              loading={loading}
              date={date}
              modifiers={modifiers}
              details={dayDetail && dayDetail.details}
            />
          );
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
    display: flex;
    background: white;
    flex-direction: column;
    width: 100%;
    align-items: center;
`;

const StyledPicker = styled(ReactDayPicker)`
  * {
    outline: none;
  }
  ${({ theme }) => css`
    .DayPicker-Months {
      justify-content: flex-start;
      padding: ${theme.spacing.padding.xs} ${theme.spacing.padding.md};
    }

    display: flex;
    flex: 1 0 14.2857%;
    flex-wrap: wrap;
    width: 100%;

    .DayPicker-wrapper {
      width: 100%;
    }
    
    .DayPicker-Day {  
      width: 100%;
      padding: 0;
      margin: 0;
      font-size: 15px;
      cursor: default;
      border-radius: 0;
      outline: none;

      &.DayPicker-Day--today {
        > div {
          border-top: 2px solid ${theme.colors.primaryDark};
        }
      }
 
      &.DayPicker-Day--hasInfo {
        cursor: pointer;

        > div {
          background: white;
        }
      }
      &.DayPicker-Day--outside {
        opacity: 0.3;
        
        > div {
          pointer-events: none;
        }
      }      
    }

    .DayPicker-Month {
      width: 100%;
      margin: 0;
    }
    .DayPicker-Week {
      display: flex;
    }
  `}
`;

const MonthNavigation = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing.margins.md};
    border-bottom: ${theme.colors.lightGrey} 1px solid;
    padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.sm};
    

    .month-navigation-inner {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }
    
    .month-name {
      font-size: ${theme.fonts.sizes.h4}
    }

    .order-button {
      justify-self: flex-end;
    }

    ${breakpoint("md", theme)} {
      padding: ${theme.spacing.padding.md} ${theme.spacing.padding.sm};

      .month-name {
        font-size: ${theme.fonts.sizes.h2};
        margin-right: ${theme.spacing.margins.md};
      }
    }
  `}
`;

const NavArrowWrapper = styled.div`
  display: flex;
`;

const ThisWeek = styled.div`
  
  b {
    font-weight: bold;
  }
`;
